import { Action, Dispatch, Middleware } from 'redux';
import { createSelector } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';

import { getInsertionsWithPendingChanges } from '@paragon/layout-models';
import { closeSnackbarsWithMessage, enqueuePermanentError } from '@paragon/notification-tools';
import { formatAsNaturalLanguageList, notNullish } from '@paragon/utilities';

import { RootState } from '..';
import { getCommittedComponentDesigns } from '../selectors/getComponentDesignsSelectors';

function addConnectionToMap(connections: Map<string, Set<string>>, a: string, b: string) {
  const existingSet = connections.get(a);
  if (existingSet) {
    existingSet.add(b);
  } else {
    connections.set(a, new Set(b));
  }
}

/**
 * Returns a map where the keys are all component design GUIDs that are used in connections, and the values are the
 * other component design GUIDs that are directly involved in that connection.
 */
const componentDesignGuidsInConnections = createSelector(
  (state: RootState) => state.jackGirderConnections.data,
  (data): ReadonlyMap<string, ReadonlySet<string>> => {
    const connections = new Map<string, Set<string>>();
    for (const connection of Object.values(data)) {
      addConnectionToMap(connections, connection.girderGuid, connection.jackGuid);
      addConnectionToMap(connections, connection.jackGuid, connection.girderGuid);
    }
    return connections;
  },
);

const projectObjectGuids = createSelector(
  // We use insertions to check the existence of a component design because we know the project will be loaded before
  // the carried component connections. The request for the component summaries is concurrent with the request for
  // carried component connections, so checking against component summaries or layout models is subject to a race
  // condition.
  getInsertionsWithPendingChanges,
  // But also check component designs since when a component is duplicated, the insertion is not added right away.
  // Including this makes it so that the warning does not briefly flash across the screen when duplicating a component
  // design (including its carried component connections) and seemed preferable to trying to adjust that logic for this
  // case.
  getCommittedComponentDesigns,
  (insertions, componentDesigns): ReadonlySet<string> =>
    new Set(
      Object.values(insertions)
        .map((insertion) => insertion.objectGuid)
        .concat(componentDesigns.map((componentDesign) => componentDesign.guid)),
    ),
);

/**
 * Returns a map where the keys are the component design GUIDs that exist in connections, but do not exist in the
 * project, and the values are the other component design GUIDs that are directly involved in those connections.
 */
const getMissingComponentDesignGuids = createSelector(
  componentDesignGuidsInConnections,
  projectObjectGuids,
  (componentDesignGuidsThatShouldExistInProject, guidsOfProjectObjects): ReadonlyMap<string, ReadonlySet<string>> => {
    const result = new Map(componentDesignGuidsThatShouldExistInProject);
    for (const guid of guidsOfProjectObjects) {
      result.delete(guid);
    }
    return result;
  },
);

const getInvalidCarriedComponentsErrorMessage = createSelector(
  getMissingComponentDesignGuids,
  (state: RootState) => state.componentDesigns.data,
  (missingComponentDesignGuids, componentDesigns): string | null => {
    if (missingComponentDesignGuids.size === 0) return null;

    const componentDesignsConnectedToMissingComponentDesigns = new Set<string>();
    for (const componentDesignsConnectedToMissingComponentDesign of missingComponentDesignGuids.values()) {
      for (const componentDesignGuid of componentDesignsConnectedToMissingComponentDesign) {
        componentDesignsConnectedToMissingComponentDesigns.add(componentDesignGuid);
      }
    }

    const componentNames = [...componentDesignsConnectedToMissingComponentDesigns]
      .map((guid) => componentDesigns[guid])
      .filter(notNullish)
      .map((componentDesign) => componentDesign.component.name);
    if (componentNames.length === 0) {
      return 'Invalid carried component relationship - contact Paragon to fix your project before continuing.';
    }

    const componentNameString = formatAsNaturalLanguageList(componentNames, '&');
    return `Component(s) ${componentNameString} have connections with components that do not exist in the project. Regenerate the component(s) or delete and recreate them to get rid of the invalid connections.`;
  },
);

let currentErrorMessage: string | null = null;

let jackGirderErrorReported = false;

export const invalidCarriedComponentConnectionMiddleware: Middleware<{}, RootState, Dispatch<Action>> =
  (store) => (next) => (action) => {
    const returnValue = next(action);

    const newState = store.getState();

    const isInProjectContext = newState.application.openContext?.type === 'project';
    if (!isInProjectContext) return returnValue;

    const desiredErrorMessage = getInvalidCarriedComponentsErrorMessage(newState);

    if (currentErrorMessage !== desiredErrorMessage) {
      // Update `currentErrorMessage` before dispatching anything, otherwise we'll get stuck in an infinite loop
      const oldErrorMessage = currentErrorMessage;
      currentErrorMessage = desiredErrorMessage;

      if (oldErrorMessage != null) {
        store.dispatch(closeSnackbarsWithMessage(oldErrorMessage));
      }

      if (desiredErrorMessage != null) {
        store.dispatch(enqueuePermanentError(desiredErrorMessage));

        if (!jackGirderErrorReported) {
          jackGirderErrorReported = true;

          Sentry.captureMessage('Component designs used in carried component relationship(s) are missing');
        }
      }
    }

    return returnValue;
  };
